/**
 * Responsive design hooks
 *
 * These hooks provide consistent responsive behavior across the application.
 * They fix the inconsistent isMobile logic that was causing bugs.
 *
 * @example
 * const availableWidth = useAvailableWidth();
 * const isMobile = useIsMobile();
 */

import { useEffect, useState } from "react";
import { isAtMostTablet } from "../constants/breakpoints";
import { getSidebarWidth } from "../constants/layout";

/**
 * Hook to get available width accounting for sidebar
 *
 * Returns full width on mobile (<=768px), width minus sidebar on desktop.
 * This fixes the inconsistent logic where different parts of the code
 * checked different breakpoints (768px vs 480px).
 *
 * @returns Available width in pixels
 */
export function useAvailableWidth(): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const update = () => {
      const currentWidth = window.innerWidth;
      const isMobile = isAtMostTablet(currentWidth);

      const available = isMobile
        ? currentWidth
        : currentWidth - getSidebarWidth(currentWidth);

      setWidth(Math.floor(available));
    };

    update();
    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("resize", update);
    };
  }, []);

  return width;
}

/**
 * Hook to get current window width
 *
 * @returns Window width in pixels
 */
export function useWindowWidth(): number {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const update = () => setWidth(window.innerWidth);

    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return width;
}

/**
 * Hook to detect if current viewport is mobile
 *
 * Mobile is consistently defined as <= tablet breakpoint (768px).
 * This provides a single source of truth for mobile detection.
 *
 * @returns true if viewport is mobile (<=768px), false otherwise
 */
export function useIsMobile(): boolean {
  const width = useWindowWidth();
  return isAtMostTablet(width);
}

/**
 * Hook to get current sidebar width based on viewport
 *
 * Returns the responsive sidebar width that scales with screen size.
 * Used to keep layout calculations in sync with the actual sidebar.
 *
 * @returns Sidebar width in pixels
 */
export function useSidebarWidth(): number {
  const width = useWindowWidth();
  return getSidebarWidth(width);
}
